import { lazy, Suspense, useMemo, useState } from "react";
import { useUserDataFlattened } from "../../../modules/user/stores";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { ROUTES } from "@/types/routes";
import {
  useActivityPopupPriority,
  useIsHomeDisplayReady,
} from "@/modules/homeDisplay/hooks";
import { ActivityType, TimingType } from "@/modules/homeDisplay/types";
import type { CashbackPopupResult } from "./useCashbackPopup.types";

const Cashback = lazy(() =>
  import("../components/Cashback/Cashback").then(m => ({
    default: m.Cashback,
  }))
);

const CASHBACK_CONFIG = POPUP_CONFIGS.CASHBACK;

const CashbackContent: React.FC<{ onComplete: () => void }> = ({
  onComplete,
}) => {
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    onComplete();
  };

  return (
    <Suspense fallback={null}>
      <Cashback visible={visible} onClose={handleClose} />
    </Suspense>
  );
};

export const useCashbackPopup = (): CashbackPopupResult => {
  const { data: userInfo } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();

  // 获取活动配置中的 priority，不存在则使用默认值
  const priority = useActivityPopupPriority(
    ActivityType.FIVE_DAILY_RECHARGE,
    CASHBACK_CONFIG.priority,
    TimingType.ACTIVITY_QUALIFICATION
  );

  const frbEndAt = useMemo(() => {
    if (typeof window === "undefined") return Date.now() + 60 * 60 * 1000;

    const now = Date.now();
    const key = `rechargeEndAt:${userInfo?.userId ?? "guest"}`;
    const savedRaw = localStorage.getItem(key);
    const saved = savedRaw ? Number(savedRaw) : 0;

    if (saved && saved > now) return saved;

    const newEndAt = now + 60 * 60 * 1000;
    localStorage.setItem(key, String(newEndAt));
    return newEndAt;
  }, [userInfo?.userId]);

  const currentPromoRatio = useMemo(() => {
    if (!userInfo?.customRechargeRules || !userInfo?.rechargeCount) {
      return null;
    }

    const currentRule = userInfo.customRechargeRules.find(
      rule => rule.count === userInfo.rechargeCount
    );

    if (
      !currentRule ||
      !currentRule.ruleList ||
      currentRule.ruleList.length === 0
    ) {
      return null;
    }

    const maxPromoRatio = Math.max(
      ...currentRule.ruleList.map(r => r.promoRatio)
    );

    return maxPromoRatio < 1
      ? Math.round(maxPromoRatio * 100)
      : Math.round(maxPromoRatio);
  }, [userInfo?.customRechargeRules, userInfo?.rechargeCount]);

  const shouldShow =
    currentPromoRatio !== null &&
    !!userInfo?.firstRechargeFlag &&
    Date.now() < frbEndAt;

  const { trigger } = useQueuedPopup({
    popupId: CASHBACK_CONFIG.id,
    priority,
    shouldShow: () =>
      isConfigReady &&
      currentPromoRatio !== null &&
      !!userInfo?.firstRechargeFlag,
    renderContent: onComplete => <CashbackContent onComplete={onComplete} />,
    showOnce: false,
    allowManualTrigger: true,
    autoTrigger: false,
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/", ROUTES.LOGIN, ROUTES.REGISTER],
    dependencies: [
      isConfigReady,
      currentPromoRatio,
      userInfo?.firstRechargeFlag,
    ],
    userBinding: true,
    getUserId: () => userInfo?.userId,
    timingType: TimingType.ACTIVITY_QUALIFICATION,
  });

  const remainingMs = Math.max(0, frbEndAt - Date.now());

  return {
    shouldShow,
    trigger,
    remainingMs,
    promoRatio: currentPromoRatio,
  };
};
